import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  forwardRef
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor
} from '@angular/forms';

export interface FormattedNumberConfig {
  decimals?: number | null;
  useThousands?: boolean;
  maxDigits?: number;
}

/**
 * FormattedNumberInputDirective
 *
 * Formats a number using:
 * - Dot (".") as thousands separator (if enabled).
 * - Comma (",") as decimal separator.
 * - A configurable number of decimals.
 *
 * The displayed value is formatted for readability, while the Angular form
 * gets the true numeric value.
 *
 * Additionally, the directive preserves the caret (cursor) position during
 * editing, with special handling to ensure that deleting the first digit
 * keeps the caret at position 0.
 *
 * Usage:
 *   <input formattedNumber [decimals]="2" ... />
 *   <input formattedNumber [decimals]="0" ... />  // integer only
 *   <input formattedNumber [decimals]="null" ... />  // no limit on decimals
 *
 * 
 *   - [useThousands] (boolean): Whether to insert thousands separators. Default is true.
 *   - [maxDigits] (number): Maximum number of digits allowed in the integer part.
 *       If not provided, it defaults to 10.
 */
@Directive({
  standalone: true,
  selector: '[formattedNumber]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormattedNumberInputDirective),
      multi: true
    }
  ]
})
export class FormattedNumberInputDirective implements ControlValueAccessor {
  // Locale-specific settings:
  // Use comma as the decimal separator.
  private decimalSeparator: string = ',';
  // Use dot as the thousands separator.
  private thousandsSeparator: string = '.';

  // Private backing field for the config
  private _config: FormattedNumberConfig = { decimals: 2, useThousands: true, maxDigits: 10 };

  // Use a setter to assign a default if the input is undefined or null.
  @Input()
  set config(value: FormattedNumberConfig | undefined) {
    this._config = value || { decimals: 2, useThousands: true, maxDigits: 10 };
  }

  get config(): FormattedNumberConfig {
    return this._config;
  }

  // Helper getters that use the config
  private get allowedDecimals(): number | null {
    return this.config.decimals === undefined ? 2 : this.config.decimals;
  }

  private get useThousandsSeparator(): boolean {
    return this.config.useThousands === undefined ? true : this.config.useThousands;
  }

  private get allowedMaxDigits(): number {
    return this.config.maxDigits === undefined ? 10 : this.config.maxDigits;
  }


  // Callbacks for Angular forms.
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  // Allowed keys: navigation keys (Backspace, Delete, etc.) are permitted.
  private allowedKeys: string[] = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'Tab'
  ];

  // Store the raw (unformatted) value before an input event.
  private previousRawValue: string = '';

  constructor(private elementRef: ElementRef<HTMLInputElement>) {}

  public writeValue(value: number | null): void {
    const input = this.elementRef.nativeElement;
    if (value == null || isNaN(value)) {
      input.value = '';
    } else {
      let valueStr: string;
      if (this.allowedDecimals !== null) {
        valueStr = value.toFixed(this.allowedDecimals);
      } else {
        valueStr = value.toString();
      }
      valueStr = valueStr.replace('.', this.decimalSeparator);
      input.value = this.formatDisplayValue(valueStr);
    }
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    if (this.allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return;
    }
    const inputEl = this.elementRef.nativeElement;
    const inputValue = inputEl.value;

    // Store the current raw value (with thousand separators removed)
    this.previousRawValue = inputValue.replace(new RegExp('\\' + this.thousandsSeparator, 'g'), '');

    // Validate maximum number of digits in the integer part if applicable.
    const selectionStart = inputEl.selectionStart || 0;
    const selectionEnd = inputEl.selectionEnd || 0;
    if (/^\d$/.test(key)) {
      // If there is a selection, allow input (since the selection will be replaced).
      if (selectionStart !== selectionEnd) {
        // Allow the keypress
      } else {
        let rawValue = inputValue;
        if (this.useThousandsSeparator) {
          rawValue = rawValue.replace(new RegExp('\\' + this.thousandsSeparator, 'g'), '');
        }
        let integerPart = rawValue;
        if (rawValue.includes(this.decimalSeparator)) {
          integerPart = rawValue.split(this.decimalSeparator)[0];
        }
        if (integerPart.startsWith('-')) {
          integerPart = integerPart.substring(1);
        }
        // Enforce max digits only if caret is in the integer portion.
        if (!inputValue.includes(this.decimalSeparator) || selectionStart <= inputValue.indexOf(this.decimalSeparator)) {
          if (integerPart.length >= this.allowedMaxDigits) {
            event.preventDefault();
            return;
          }
        }
      }
    }

    // If the user pressed a comma or dot, allow it only if a decimal separator isn’t already present.
    if (key === ',' || key === '.') {
      if (this.allowedDecimals === 0) {
        event.preventDefault();
        return;
      }
      if (inputValue.includes(this.decimalSeparator)) {
        event.preventDefault();
      }
      return;
    }

    // If the input already has a decimal separator and the caret is in the fractional part,
    // ensure no more than the configured number of digits are allowed after the separator.
    if (inputValue.includes(this.decimalSeparator) && this.allowedDecimals !== null) {
      const decimalIndex = inputValue.indexOf(this.decimalSeparator);
      const selectionStart = inputEl.selectionStart || 0;
      if (selectionStart > decimalIndex) {
        const decimalPart = inputValue.substring(decimalIndex + 1);
        if (decimalPart.length >= this.allowedDecimals) {
          event.preventDefault();
          return;
        }
      }
    }

    // Allow only digit keys.
    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = this.elementRef.nativeElement;

    // 1. Capture the current caret position and the current input value.
    const oldSelectionStart = input.selectionStart || 0;
    const oldSelectionEnd = input.selectionEnd || 0;
    const oldValue = input.value;

    // 2. Remove any thousands separators.
    let value: string = oldValue.replace(new RegExp('\\' + this.thousandsSeparator, 'g'), '');

    // 3. Standardize the decimal separator.
    if (value.indexOf(this.decimalSeparator) === -1) {
      value = value.replace('.', this.decimalSeparator);
    }

    // 4. Limit the fractional part to the configured number of digits.
    if (value.includes(this.decimalSeparator)) {
      const parts = value.split(this.decimalSeparator);
      const integerPart = parts[0];
      let decimalPart = parts[1] || '';
      if (this.allowedDecimals === 0) {
        value = integerPart;
      } else if (this.allowedDecimals !== null) {
        if (decimalPart.length > this.allowedDecimals) {
          decimalPart = decimalPart.slice(0, this.allowedDecimals);
        }
        value = integerPart + this.decimalSeparator + decimalPart;
      }
    }

    // 5. If the value starts with the decimal separator, prepend a "0".
    if (value.startsWith(this.decimalSeparator)) {
      value = '0' + value;
    }

    // 6. Convert the value for numeric parsing (replace comma with dot).
    const numericString = value.replace(this.decimalSeparator, '.');
    let numericValue: number | null = parseFloat(numericString);
    if (isNaN(numericValue)) {
      numericValue = null;
    }
    this.onChange(numericValue);

    // 7. Reformat the value with thousands separators.
    const formattedValue = this.formatDisplayValue(value);
    input.value = formattedValue;

    // 8. --- CARET RESTORATION ---
    // If the entire text was selected, preserve the full selection.
    if (oldSelectionStart === 0 && oldSelectionEnd === oldValue.length) {
      setTimeout(() => {
        input.setSelectionRange(0, formattedValue.length);
      });
      return;
    }

    let newCursorPosition = 0;
    const oldDecimalIndex = oldValue.indexOf(this.decimalSeparator);
    const newDecimalIndex = formattedValue.indexOf(this.decimalSeparator);

    if (oldDecimalIndex !== -1 && oldSelectionStart > oldDecimalIndex) {
      const offsetFromDecimal = oldSelectionStart - oldDecimalIndex;
      if (newDecimalIndex !== -1) {
        newCursorPosition = newDecimalIndex + offsetFromDecimal;
      } else {
        newCursorPosition = formattedValue.length;
      }
    } else {
      const digitsBeforeCursor = oldValue.substring(0, oldSelectionStart).replace(/\D/g, '').length;
      if (digitsBeforeCursor === 0) {
        newCursorPosition = 0;
      } else {
        let digitCount = 0;
        for (let i = 0; i < formattedValue.length; i++) {
          if (/\d/.test(formattedValue.charAt(i))) {
            digitCount++;
          }
          if (digitCount >= digitsBeforeCursor) {
            newCursorPosition = i + 1;
            break;
          }
        }
        if (newCursorPosition === 0) {
          newCursorPosition = oldSelectionStart;
        }
      }
    }

    setTimeout(() => {
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    });

    this.previousRawValue = value;
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
    const input = this.elementRef.nativeElement;
    if (input.value.trim() !== '') {
      let value = input.value.replace(new RegExp('\\' + this.thousandsSeparator, 'g'), '');
      value = value.replace(this.decimalSeparator, '.');
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        if (this.allowedDecimals !== null) {
          let fixedValue = numericValue.toFixed(this.allowedDecimals);
          fixedValue = fixedValue.replace('.', this.decimalSeparator);
          input.value = this.formatDisplayValue(fixedValue);
        } else {
          let fixedValue = numericValue.toString().replace('.', this.decimalSeparator);
          input.value = this.formatDisplayValue(fixedValue);
        }
      }
    }
  }

  private formatDisplayValue(value: string): string {
    const parts = value.split(this.decimalSeparator);
    let integerPart = parts[0];
    const decimalPart = parts.length > 1 ? parts[1] : '';

    const isNegative = integerPart.startsWith('-');
    if (isNegative) {
      integerPart = integerPart.substring(1);
    }

    if (this.useThousandsSeparator) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator);
    }
    if (isNegative) {
      integerPart = '-' + integerPart;
    }

    let formatted = integerPart;
    if (value.indexOf(this.decimalSeparator) !== -1) {
      formatted += this.decimalSeparator + decimalPart;
    }
    return formatted;
  }
}